if (!customElements.get('product-measurements')) {
  customElements.define(
    'product-measurements',
    class ProductMeasurements extends HTMLElement {
      connectedCallback() {
        this.infoWrapper = this.closest('.product__info-wrapper');
        this.numberInputs = Array.from(this.querySelectorAll('input[type="number"][name^="properties["]'));
        this.widthInputs = this.numberInputs.filter((input) => input.classList.contains('width_input'));
        this.heightInputs = this.numberInputs.filter((input) => input.classList.contains('height_input'));
        this.cassetteInputs = Array.from(
          this.querySelectorAll('input[type="radio"][name="properties[Cassette Mounted]"]')
        );
        this.validationReported = false;
        this.requiredMeasurements = undefined;
        const variantData = this.querySelector('[data-measurement-variants]');
        this.sizing = variantData ? JSON.parse(variantData.textContent) : null;
        this.sizing?.variants.forEach((variant) => {
          [variant.width, variant.height] = (variant.sku || '').split('x').map(Number);
        });

        this.onFieldInput = this.onFieldInput.bind(this);
        this.onFormSubmit = this.onFormSubmit.bind(this);
        this.addEventListener('input', this.onFieldInput);
        this.addEventListener('change', this.onFieldInput);
        this.addEventListener('focusout', this.onFieldInput);

        this.form = document.getElementById(this.dataset.formId);
        this.form?.addEventListener('submit', this.onFormSubmit, { capture: true });

        if (this.infoWrapper) {
          this.variantObserver = new MutationObserver(() => this.updateVariantState());
          this.variantObserver.observe(this.infoWrapper, {
            attributes: true,
            attributeFilter: ['data-variant', 'data-vwidth', 'data-vheight'],
          });
        }

        this.updateVariantState();
      }

      disconnectedCallback() {
        this.removeEventListener('input', this.onFieldInput);
        this.removeEventListener('change', this.onFieldInput);
        this.removeEventListener('focusout', this.onFieldInput);
        this.form?.removeEventListener('submit', this.onFormSubmit, { capture: true });
        this.variantObserver?.disconnect();
      }

      onFormSubmit(event) {
        if (this.reportValidity()) return;

        event.preventDefault();
        event.stopImmediatePropagation();
      }

      onFieldInput(event) {
        if (!this.numberInputs.includes(event.target) && !this.cassetteInputs.includes(event.target)) return;
        if (this.numberInputs.includes(event.target)) {
          this.editingMeasurement = event.type === 'input';
          if (!this.editingMeasurement) this.selectFittingSize();
        }
        this.validate(this.validationReported);
      }

      getFittingVariant() {
        if (!this.sizing || !this.requiredMeasurements || !this.numberInputs.every(
          (input) => input.value !== '' && Number.isInteger(Number(input.value)) && Number(input.value) > 0 && Number(input.value) >= Number(input.min)
        )) return null;

        const current = this.closest('product-info').getSelectedVariant(this.infoWrapper);
        if (!current) return null;
        const width = Math.max(...this.widthInputs.map((input) => Number(input.value)));
        const height = Math.max(...this.heightInputs.map((input) => Number(input.value)));
        return this.sizing.variants.filter((variant) =>
          variant.width >= width && variant.height >= height &&
          variant.options.every((option, index) => index === this.sizing.sizeIndex || option === current.options[index])
        ).sort((a, b) => a.width * a.height - b.width * b.height)[0] || null;
      }

      async selectFittingSize() {
        // A response from an earlier edit must not select a kit from a half-typed number.
        if (this.selectingSize || this.editingMeasurement) return;
        const productInfo = this.closest('product-info');
        if (productInfo?.pendingRequestUrl) return;
        const variant = this.getFittingVariant();
        const current = productInfo?.getSelectedVariant(this.infoWrapper);
        const status = this.querySelector('.measurement-selection-status');
        if (!variant || !variant.available) {
          status?.classList.add('hidden');
          return;
        }
        const message = `${variant.options[this.sizing.sizeIndex]} selected to fit your measurements. Total: ${variant.total}.`;
        if (variant.id === current?.id) {
          status.textContent = message;
          return;
        }

        this.selectingSize = true;
        status.textContent = 'Updating size and price…';
        status.classList.remove('hidden');
        productInfo.resetProductFormState();
        const updated = await productInfo.renderProductInfo({
          requestUrl: `${productInfo.dataset.url}?variant=${variant.id}&section_id=${productInfo.sectionId}`,
          targetId: null,
          callback: productInfo.handleUpdateProductInfo(productInfo.dataset.url),
        });
        this.selectingSize = false;
        if (!this.isConnected) return;
        if (!updated || productInfo.getSelectedVariant(this.infoWrapper)?.id !== variant.id) {
          status.textContent = 'Unable to update the size. Please try again before adding to cart.';
          if (!productInfo.pendingRequestUrl) productInfo.productForm?.toggleSubmitButton(false);
          return;
        }
        status.textContent = message;
        status.classList.toggle('hidden', !this.requiredMeasurements);
        this.selectFittingSize();
        this.validate(this.validationReported);
      }

      updateVariantState() {
        const wasRequired = this.requiredMeasurements;
        this.requiredMeasurements = this.requiresMeasurements();
        this.classList.toggle('hidden', !this.requiredMeasurements);

        [...this.numberInputs, ...this.cassetteInputs].forEach((input) => {
          input.required = this.requiredMeasurements;
          input.setAttribute('aria-required', String(this.requiredMeasurements));
        });

        if (!this.requiredMeasurements) {
          this.clearValues();
          this.validationReported = false;
          this.clearValidationState();
          return;
        }

        this.updateMaximums();
        this.selectFittingSize();
        if (wasRequired === false || wasRequired === undefined) {
          this.validationReported = false;
          this.clearValidationState();
        } else {
          this.validate(this.validationReported);
        }
      }

      requiresMeasurements() {
        const selectedCuttingService = this.infoWrapper?.querySelector(
          '.product-form-Cutting input[type="radio"]:checked, .product-form-Cutting select option:checked'
        );

        if (selectedCuttingService) return selectedCuttingService.value === 'Yes';
        return this.infoWrapper?.dataset.variant?.includes('Yes') ?? false;
      }

      updateMaximums() {
        const sizes = this.sizing?.variants.filter((variant) => variant.width > 0 && variant.height > 0);
        const maxWidth = sizes?.length ? Math.max(...sizes.map((variant) => variant.width)) : this.getVariantMaximum('vwidth');
        const maxHeight = sizes?.length ? Math.max(...sizes.map((variant) => variant.height)) : this.getVariantMaximum('vheight');

        this.widthInputs.forEach((input) => {
          input.max = String(maxWidth ?? Number(input.dataset.defaultMax));
        });
        this.heightInputs.forEach((input) => {
          input.max = String(maxHeight ?? Number(input.dataset.defaultMax));
        });

        const maxWidthMessage = this.querySelector('.max-width-msg');
        const maxHeightMessage = this.querySelector('.max-height-msg');
        if (maxWidthMessage) maxWidthMessage.textContent = this.widthInputs[0]?.max || '';
        if (maxHeightMessage) maxHeightMessage.textContent = this.heightInputs[0]?.max || '';
      }

      getVariantMaximum(datasetKey) {
        const value = this.infoWrapper?.dataset[datasetKey];
        if (!value) return null;

        const number = Number(value);
        return Number.isFinite(number) ? number : null;
      }

      clearValues() {
        this.numberInputs.forEach((input) => {
          input.value = '';
        });
        this.cassetteInputs.forEach((input) => {
          input.checked = false;
        });
      }

      clearValidationState() {
        [...this.numberInputs, ...this.cassetteInputs].forEach((input) => {
          input.classList.remove('input-error');
          input.removeAttribute('aria-invalid');
        });

        this.querySelectorAll(
          '.measurement-required-error, .measurement-range-error, .measurement-format-error, .measurement-width-min-error, .measurement-height-min-error, .radio-error-msg, .error-message--height-difference, .measurement-availability-error, .measurement-selection-status'
        ).forEach((message) => message.classList.add('hidden'));
      }

      validate(showErrors = false) {
        if (!this.requiredMeasurements) {
          this.clearValidationState();
          return true;
        }

        const missingInputs = this.numberInputs.filter((input) => input.value.trim() === '');
        const formatInputs = this.numberInputs.filter((input) => {
          if (input.value.trim() === '') return false;
          const value = Number(input.value);
          return !Number.isFinite(value) || !Number.isInteger(value);
        });
        const rangeInputs = this.numberInputs.filter((input) => {
          if (input.value.trim() === '') return false;
          const value = Number(input.value);
          const min = Number(input.min);
          const max = Number(input.max);
          return Number.isFinite(value) && (value < min || value > max);
        });

        const heightsAreValid = this.heightInputs.every(
          (input) =>
            input.value.trim() !== '' &&
            Number.isInteger(Number(input.value)) &&
            !rangeInputs.includes(input)
        );
        const shortHeightInputs = this.heightInputs.filter(
          (input) => input.value.trim() !== '' && Number(input.min) > 0 && Number(input.value) < Number(input.min)
        );
        const shortWidthInputs = this.widthInputs.filter(
          (input) => input.value.trim() !== '' && Number(input.min) > 0 && Number(input.value) < Number(input.min)
        );
        const belowMinimumInputs = [...shortHeightInputs, ...shortWidthInputs];
        const heightDifferenceInvalid =
          heightsAreValid && Math.abs(Number(this.heightInputs[0].value) - Number(this.heightInputs[1].value)) > 11;
        const cassetteMissing = this.cassetteInputs.length > 0 && !this.cassetteInputs.some((input) => input.checked);
        const fittingVariant = this.getFittingVariant();
        const noSizeFits = this.sizing && !fittingVariant && belowMinimumInputs.length === 0 && this.numberInputs.every(
          (input) => input.value !== '' && Number.isInteger(Number(input.value)) && Number(input.value) > 0
        );
        const unavailable = fittingVariant && !fittingVariant.available;
        const sizeMismatch = fittingVariant && fittingVariant.id !== this.closest('product-info').getSelectedVariant(this.infoWrapper)?.id;

        const invalidInputs = new Set([...missingInputs, ...formatInputs, ...rangeInputs]);
        if (heightDifferenceInvalid) this.heightInputs.forEach((input) => invalidInputs.add(input));

        this.numberInputs.forEach((input) => {
          const isInvalid = invalidInputs.has(input);
          input.classList.toggle('input-error', showErrors && isInvalid);
          if (showErrors && isInvalid) {
            input.setAttribute('aria-invalid', 'true');
          } else {
            input.removeAttribute('aria-invalid');
          }
        });
        this.cassetteInputs.forEach((input) => {
          input.classList.toggle('input-error', showErrors && cassetteMissing);
          if (showErrors && cassetteMissing) {
            input.setAttribute('aria-invalid', 'true');
          } else {
            input.removeAttribute('aria-invalid');
          }
        });

        this.toggleMessage('.measurement-required-error', showErrors && missingInputs.length > 0);
        this.toggleMessage('.measurement-format-error', showErrors && formatInputs.length > 0);
        this.toggleMessage('.measurement-width-min-error', showErrors && shortWidthInputs.length > 0);
        this.toggleMessage('.measurement-height-min-error', showErrors && shortHeightInputs.length > 0);
        this.toggleMessage('.measurement-range-error', showErrors && (rangeInputs.some((input) => !belowMinimumInputs.includes(input)) || noSizeFits));
        this.toggleMessage('.error-message--height-difference', showErrors && heightDifferenceInvalid);
        this.toggleMessage('.radio-error-msg', showErrors && cassetteMissing);
        this.toggleMessage('.measurement-availability-error', Boolean(unavailable));
        this.toggleMessage('.only-single-door', this.widthInputs.some((input) => Number(input.value) > Number(input.max)));

        return invalidInputs.size === 0 && !cassetteMissing && !unavailable && !sizeMismatch && !noSizeFits &&
          !this.selectingSize && !this.closest('product-info')?.pendingRequestUrl;
      }

      toggleMessage(selector, show) {
        this.querySelector(selector)?.classList.toggle('hidden', !show);
      }

      reportValidity() {
        this.validationReported = true;
        this.editingMeasurement = false;
        this.selectFittingSize();
        const isValid = this.validate(true);
        if (isValid) return true;

        const firstInvalidInput = [...this.numberInputs, ...this.cassetteInputs].find(
          (input) => input.getAttribute('aria-invalid') === 'true'
        );
        firstInvalidInput?.focus();
        return false;
      }
    }
  );
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        if (!this.measurementsAreValid()) return;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      measurementsAreValid() {
        const productInfo = this.closest('product-info');
        const measurements = Array.from(productInfo?.querySelectorAll('product-measurements') || []).find(
          (element) => element.dataset.formId === this.form.id
        );

        return !measurements || measurements.reportValidity();
      }
    }
  );
}
